import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, Play, Wrench } from "lucide-react";

import { hostelService, type Hostel, type HostelMaintenance, type Room } from "../../services/hostelService";
import { apiErrorMessage } from "../../utils/apiError";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ReasonPanel, errorLine } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { MAINTENANCE_TYPES, PRIORITIES, TAKES_ROOM_OFFLINE, day, labelFrom, maintenanceConfig, maintenanceStatus, money, priorityDot } from "./hostelLogic";

function RequestForm({ hostels, rooms, onClose, onDone }: { hostels: Hostel[]; rooms: Room[]; onClose: () => void; onDone: (row: HostelMaintenance) => void }) {
  const [hostelId, setHostelId] = useState(hostels.length === 1 ? String(hostels[0].id) : "");
  const [roomId, setRoomId] = useState("");
  const [kind, setKind] = useState("repair");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const roomsHere = rooms.filter((row) => String(row.hostel) === hostelId);
  const room = roomsHere.find((row) => String(row.id) === roomId);
  const save = useMutation({ mutationFn: hostelService.requestMaintenance, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    save.mutate({
      hostel: Number(hostelId),
      room: roomId ? Number(roomId) : null,
      maintenance_type: kind,
      priority: String(data.get("priority")),
      title: title.trim(),
      description: description.trim(),
      estimated_cost: String(data.get("estimated_cost") ?? "").trim() || null,
    });
  };
  return (
    <FormModal
      id="hostel-repair"
      title="Report something to fix"
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The request could not be sent.") : undefined}
      saveLabel="Send the request"
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Where" divided={false}>
        <Field label="Residence">
          <select required value={hostelId} onChange={(event) => { setHostelId(event.target.value); setRoomId(""); }} className="input mt-1">
            <option value="">Choose a residence</option>
            {hostels.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </Field>
        <Field label="Room" optional>
          <select value={roomId} disabled={!hostelId} onChange={(event) => setRoomId(event.target.value)} className="input mt-1">
            <option value="">Whole residence or a shared area</option>
            {roomsHere.map((row) => <option key={row.id} value={row.id}>Room {row.room_number}</option>)}
          </select>
        </Field>
      </FormSection>
      <FormSection title="What">
        <Field label="Kind">
          <select value={kind} onChange={(event) => setKind(event.target.value)} className="input mt-1">
            {MAINTENANCE_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Priority" hint="Urgent means unsafe or no water, power or locks.">
          <select name="priority" defaultValue="medium" className="input mt-1">
            {PRIORITIES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        {room && TAKES_ROOM_OFFLINE.includes(kind) && room.current_occupancy > 0 && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900 sm:col-span-2">
            A {labelFrom(MAINTENANCE_TYPES, kind).toLowerCase()} takes the room out of use: its {room.current_occupancy} {room.current_occupancy === 1 ? "boarder" : "boarders"} must move before the work starts.
          </p>
        )}
        <Field label="Title" className="sm:col-span-2" hint="e.g. Shower in block B not draining.">
          <input required value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} className="input mt-1" />
        </Field>
        <Field label="Describe it" as="div" className="sm:col-span-2">
          <AIAssistField
            rows={3}
            value={description}
            onChange={(next) => setDescription(next)}
            placeholder="What is wrong, where exactly, since when."
            context={{ key: "hostel.note", params: { kind: "maintenance", hostel: hostelId || undefined, room: roomId || undefined, title, maintenance_type: kind } }}
            draftLabel="Help me write it"
            draftHint="Jot what you saw; it writes it up without guessing the fault."
          />
        </Field>
        <Field label="Estimated cost (UGX)" optional>
          <input type="number" name="estimated_cost" min={0} step="any" className="input mt-1 tabular-nums" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Repairs and cleaning in the residences, from the report to the sign-off. */
export function RepairsPanel({ jobs, hostels, rooms, isLoading, isError, onRetry, onChanged }: {
  jobs?: HostelMaintenance[];
  hostels: Hostel[];
  rooms: Room[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [work, setWork] = useState("");
  const [materials, setMaterials] = useState("");
  const [cost, setCost] = useState("");
  const [checked, setChecked] = useState(false);
  const done = (text: string, close = false) => {
    setNotice({ tone: "success", text });
    if (close) setViewing(null);
    onChanged();
  };
  const start = useMutation({ mutationFn: hostelService.startMaintenance, onSuccess: (row) => done(`Work on “${row.title}” has started.`) });
  const finish = useMutation({ mutationFn: hostelService.completeMaintenance, onSuccess: (row) => done(`“${row.title}” is done.`, true) });
  const cancel = useMutation({ mutationFn: hostelService.cancelMaintenance, onSuccess: (row) => done(`“${row.title}” was cancelled.`, true) });

  const columns: RegisterColumn<HostelMaintenance>[] = [
    { header: "Reported", cell: (row) => day(row.requested_date), exportValue: (row) => day(row.requested_date), className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Job", cell: (row) => <span className="font-medium text-gray-900">{row.title}</span>, exportValue: (row) => row.title, className: "min-w-[12rem] whitespace-normal" },
    { header: "Where", cell: (row) => (row.room ? `${row.hostel_name}, room ${rooms.find((room) => room.id === row.room)?.room_number ?? ""}` : `${row.hostel_name}, shared`), exportValue: (row) => row.room_info || row.hostel_name, className: "text-gray-700" },
    { header: "Kind", cell: (row) => labelFrom(MAINTENANCE_TYPES, row.maintenance_type), exportValue: (row) => labelFrom(MAINTENANCE_TYPES, row.maintenance_type), className: "text-gray-700" },
    { header: "Priority", cell: (row) => <StatusDot label={labelFrom(PRIORITIES, row.priority)} dot={priorityDot(row.priority)} />, exportValue: (row) => labelFrom(PRIORITIES, row.priority) },
    { header: "Cost", cell: (row) => money(row.actual_cost ?? row.estimated_cost) || <span className="text-gray-300">—</span>, exportValue: (row) => Number(row.actual_cost ?? row.estimated_cost ?? 0), align: "right" },
    { header: "Status", cell: (row) => <StatusDot {...maintenanceStatus(row)} />, exportValue: (row) => maintenanceStatus(row).label },
  ];

  const job = viewing !== null ? jobs?.find((row) => row.id === viewing) : undefined;
  const room = job?.room ? rooms.find((row) => row.id === job.room) : undefined;
  const mustEmpty = job && job.status === "pending" && room && TAKES_ROOM_OFFLINE.includes(job.maintenance_type) && room.current_occupancy > 0;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Repairs"
        description="Open a job to start it, sign it off or cancel it."
        actions={
          <button type="button" disabled={!hostels.length} onClick={() => setAdding(true)} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <Wrench className="mr-1.5 h-3.5 w-3.5" /> Report something to fix
          </button>
        }
        rows={jobs}
        config={maintenanceConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          [start, finish, cancel].forEach((mutation) => mutation.reset());
          setWork("");
          setMaterials("");
          setCost("");
          setChecked(false);
          setViewing(row.id);
        }}
        openLabel={(row) => (row.status === "pending" ? "Start" : row.status === "in_progress" ? "Sign off" : "Open")}
        noun={["job", "jobs"]}
        exportName={{ file: "boarding-repairs", title: "Boarding Repairs" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="Nothing reported for repair."
      />

      {job && (
        <DetailsModal
          title={job.title}
          headline={{ label: labelFrom(MAINTENANCE_TYPES, job.maintenance_type), value: labelFrom(PRIORITIES, job.priority), note: `${job.room ? `${job.hostel_name}, room ${room?.room_number ?? ""}` : `${job.hostel_name}, shared area`} · reported ${day(job.requested_date)} by ${job.requested_by_name}` }}
          status={maintenanceStatus(job)}
          facts={[
            ["Estimated cost", money(job.estimated_cost) || undefined],
            ["Started", day(job.scheduled_date) || undefined],
            ["Being done by", job.assigned_to_name],
            ["Done", job.completed_date ? `${day(job.completed_date)} by ${job.completed_by_name}` : undefined],
            ["Cost", money(job.actual_cost) || undefined],
            ["Checked", job.status === "completed" ? (job.quality_check ? "Yes" : "Not checked") : undefined],
          ]}
          sections={[
            { title: "What is wrong", body: job.description },
            { title: "Work done", body: job.work_performed },
            { title: "Materials used", body: job.materials_used },
            { title: "Why it was cancelled", body: job.cancellation_reason, tone: "danger" },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {job.status === "pending" && (
                <button type="button" disabled={start.isPending || Boolean(mustEmpty)} onClick={() => start.mutate(job.id)} className="btn btn-primary gap-1.5">
                  <Play className="h-4 w-4" /> Work has started
                </button>
              )}
            </>
          }
        >
          {mustEmpty && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">Room {room?.room_number} still has {room?.current_occupancy} {room?.current_occupancy === 1 ? "boarder" : "boarders"}. Move them before this work starts: it takes the room out of use.</p>
          )}
          {start.isError && errorLine(start.error, "The work could not be started.")}
          {job.status === "in_progress" && (
            <section className="space-y-3 rounded-lg border border-gray-200 p-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-900">Sign it off</h4>
                <p className="mt-0.5 text-xs text-gray-500">{job.room && TAKES_ROOM_OFFLINE.includes(job.maintenance_type) ? "The room goes back into use." : "Record what was done."}</p>
              </div>
              <Field label="Work done">
                <textarea required rows={2} value={work} onChange={(event) => setWork(event.target.value)} className="input mt-1" />
              </Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Materials used" optional>
                  <input value={materials} onChange={(event) => setMaterials(event.target.value)} className="input mt-1" />
                </Field>
                <Field label="Cost (UGX)" optional>
                  <input type="number" min={0} step="any" value={cost} onChange={(event) => setCost(event.target.value)} className="input mt-1 tabular-nums" />
                </Field>
              </div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input type="checkbox" checked={checked} onChange={(event) => setChecked(event.target.checked)} className="h-4 w-4 rounded border-gray-300" /> Checked and working
              </label>
              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={!work.trim() || finish.isPending}
                  onClick={() => finish.mutate({ id: job.id, work_performed: work.trim(), materials_used: materials.trim() || undefined, actual_cost: cost || undefined, quality_check: checked })}
                  className="btn btn-primary gap-1.5"
                >
                  <CheckCircle2 className="h-4 w-4" /> Sign it off
                </button>
              </div>
              {finish.isError && errorLine(finish.error, "The job could not be signed off.")}
            </section>
          )}
          {(job.status === "pending" || job.status === "in_progress") && (
            <ReasonPanel
              title="Cancel the job"
              placeholder="e.g. Fixed by the plumber on his routine visit."
              context={{ key: "hostel.note", params: { kind: "maintenance_cancel", id: job.id } }}
              button="Cancel the job"
              danger
              busy={cancel.isPending}
              error={cancel.error}
              errorText="The job could not be cancelled."
              onSubmit={(reason) => cancel.mutate({ id: job.id, reason })}
            />
          )}
        </DetailsModal>
      )}

      {adding && (
        <RequestForm
          hostels={hostels.filter((row) => row.is_active)}
          rooms={rooms}
          onClose={() => setAdding(false)}
          onDone={(saved) => {
            setAdding(false);
            done(`“${saved.title}” reported.`);
            setViewing(saved.id);
          }}
        />
      )}
    </div>
  );
}
